import MiaOrb from '@/components/MiaOrb';
import Button from '@/components/ui/button';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { cn } from '@/lib/utils';

type Prompt = {
  /** Section label above the prompt — only the first, second and last have one. */
  label?: string;
  text: string;
  /** The closing prompt is tinted green in the prototype. */
  highlight?: boolean;
};

function SparkleIcon() {
  return (
    <svg
      width='14'
      height='14'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
      aria-hidden='true'
      className='shrink-0'
    >
      <path d='M12 3l1.9 4.6L18.5 9l-4.6 1.9L12 15l-1.9-4.1L5.5 9l4.6-1.4z' />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg
      width='15'
      height='15'
      viewBox='0 0 24 24'
      fill='none'
      stroke='#3a423d'
      strokeWidth='2.4'
      strokeLinecap='round'
      aria-hidden='true'
    >
      <path d='M6 6l12 12M18 6L6 18' />
    </svg>
  );
}

function buildPrompts(child: string): Prompt[] {
  const them = child || 'them';
  const they = child || 'they';
  return [
    { label: 'Open with', text: 'Who you are, and how you know the family.' },
    {
      label: 'Then one of these',
      text: `A moment you remember from the day ${they} arrived.`,
    },
    { text: `Something you hope for ${them} by the time they turn 18.` },
    { text: 'One piece of advice you wish someone had told you.' },
    {
      label: 'Close with',
      text: `Your name, so ${they} ${child ? 'knows' : 'know'} whose voice this is.`,
      highlight: true,
    },
  ];
}

type RecordingIdeasDialogProps = {
  childFirstName?: string;
};

/**
 * "Not sure what to say?" prompts for the idle recorders (GIFTWEB-23).
 *
 * Built on Sheet (Radix Dialog): bottom sheet under 1024px, centred modal
 * above, with the focus trap, Escape-to-close and focus-return-to-trigger
 * that the AC asks for coming from the primitive rather than by hand.
 */
function RecordingIdeasDialog({ childFirstName }: RecordingIdeasDialogProps) {
  const child = childFirstName?.trim() || '';
  const prompts = buildPrompts(child);

  return (
    <Sheet>
      <SheetTrigger asChild>
        <button
          type='button'
          data-testid='recording-ideas-trigger'
          className='mt-[13px] inline-flex cursor-pointer items-center gap-1.5 rounded-sm border-none bg-transparent p-0 text-[12.5px] font-bold text-[#256956] focus-visible:ring-[3px] focus-visible:ring-[#66bda4]/60 focus-visible:outline-none'
        >
          <SparkleIcon />
          Not sure what to say?
        </button>
      </SheetTrigger>

      <SheetContent
        showCloseButton={false}
        data-testid='recording-ideas-dialog'
        className='gap-0 lg:max-w-[680px]'
      >
        <div className='flex items-start gap-3 text-left'>
          <div className='flex size-11 shrink-0 items-center justify-center'>
            <MiaOrb size={44} mood='idle' />
          </div>
          <div className='min-w-0 flex-1'>
            <SheetTitle className='text-lg leading-[1.25] font-bold tracking-[-0.015em] text-[#0f2922]'>
              Not sure what to say?
            </SheetTitle>
            <SheetDescription className='mt-1 text-[13px] leading-[1.5] text-[#5a635e]'>
              Thirty seconds goes quickly. Pick one thought and say it plainly –
              that is the bit {child || 'they'} will treasure.
            </SheetDescription>
          </div>
          <SheetClose asChild>
            <button
              type='button'
              aria-label='Close'
              data-testid='recording-ideas-close'
              className='flex size-[30px] shrink-0 cursor-pointer items-center justify-center rounded-full border-none bg-[#f1efe9] p-0'
            >
              <CloseIcon />
            </button>
          </SheetClose>
        </div>

        <ul className='mt-[18px] flex flex-col gap-[9px] text-left'>
          {prompts.map((prompt) => (
            <li
              key={prompt.text}
              className={cn(
                'rounded-[14px] border px-3.5 py-3',
                prompt.highlight
                  ? 'border-[#d8e7df] bg-[#eef4f0]'
                  : 'border-[#ebe7df] bg-[#fbfaf9]',
              )}
            >
              {prompt.label && (
                <p
                  className={cn(
                    'text-[10.5px] font-bold tracking-[0.07em] uppercase',
                    prompt.highlight ? 'text-[#256956]' : 'text-[#8b8a88]',
                  )}
                >
                  {prompt.label}
                </p>
              )}
              <p
                className={cn(
                  'text-sm leading-[1.45]',
                  prompt.highlight ? 'text-[#0f2922]' : 'text-[#1a201d]',
                  prompt.label && 'mt-[3px]',
                )}
              >
                {prompt.text}
              </p>
            </li>
          ))}
        </ul>

        <SheetClose asChild>
          <Button
            type='button'
            data-testid='recording-ideas-confirm'
            className='mt-[18px] h-[50px] w-full text-[15.5px]'
          >
            Got it – let's record
          </Button>
        </SheetClose>
      </SheetContent>
    </Sheet>
  );
}

export default RecordingIdeasDialog;
